import { IosArrowRight } from '@sgcmpteltd/uikit-react';
import cx from 'clsx';
import Image from 'next/image';
import { useContext, useState } from 'react';

import type {
  CamelCaseParallelDealer,
} from '@/apis/server/new-car/v1/car-detail';
import { ImageWithFallback } from '@/components/image-with-fallback';
import { ArfModal } from '@/components/modals/arf-modal';
import { DepreciationModal } from '@/components/modals/depreciation-modal';
import { DownpaymentModal } from '@/components/modals/downpayment-modal';
import { InstallmentModal } from '@/components/modals/installment-modal';
import { OmvModal } from '@/components/modals/omv-modal';
import { PremiumModal } from '@/components/modals/premium-modal';
import { RemarkModal } from '@/components/modals/remark-modal';
import { RoadTaxModal } from '@/components/modals/road-tax-modal';
import {
  TotalBasicCostModal,
} from '@/components/modals/total-basic-cost-modal';
import { VesModal } from '@/components/modals/ves-modal';
import { Tooltip } from '@/components/tooltip';
import { VEHICLE_VALUE_ELECTRIC_CARS } from '@/constants/global';
import { InfoContext } from '@/context/info';
import { joinSgcmMedia, joinSgcmSite } from '@/utils/url/join/public';

import { ICON_SIZE_IN_PIXEL } from '../../constants';
import { PiPriceInfoList } from '../pi-price-list';
import {
  PRICE_INFO_SPAN_CONTENT_REBATE,
  PRICE_INFO_SPAN_CONTENT_ROCORDED_PRICE,
  PRICE_INFO_SPAN_CONTENT_SURCHARGE,
  PRICE_INFO_TITLE_ARF,
  PRICE_INFO_TITLE_COE_QUOTA_PREMIUM,
  PRICE_INFO_TITLE_CURRENT_PRICE,
  PRICE_INFO_TITLE_DEPRECIATION,
  PRICE_INFO_TITLE_DOWNPAYMENT,
  PRICE_INFO_TITLE_EEAI,
  PRICE_INFO_TITLE_HIGHEST_RECORDED_PRICE,
  PRICE_INFO_TITLE_INSTALMENT,
  PRICE_INFO_TITLE_LOWEST_RECORDED_PRICE,
  PRICE_INFO_TITLE_OMV,
  PRICE_INFO_TITLE_PREMIUM,
  PRICE_INFO_TITLE_PREVIOUS_RECORDED_PRICE,
  PRICE_INFO_TITLE_ROAD_TAX,
  PRICE_INFO_TITLE_TOTAL_BASIC_COST,
  PRICE_INFO_TITLE_VES,
  PRICE_SIGN_ICON_HEIGHT,
  PRICE_SIGN_ICON_WIDTH,
} from './constants';
import styles from './styles.module.scss';
import type { Component } from './types';

export const PriceInfoTable: Component = ({
  submodel,
  isPiModel,
  piDealers,
  submodelFullName,
}) => {
  const {
    id: submodelId, price, depreciation, withCoe, priceDetail, priceFluctuation,
  } = submodel;

  const { carDetail } = useContext(InfoContext);

  const [
    isDownpaymentModalOpen,
    setIsDownpaymentModalOpen,
  ] = useState(false);
  const [
    isDepreciationModalOpen,
    setIsDepreciationModalOpen,
  ] = useState(false);
  const [
    isInstallmentModalOpen,
    setIsInstallmentModalOpen,
  ] = useState(false);
  const [
    isRoadTaxModalOpen,
    setIsRoadTaxModalOpen,
  ] = useState(false);
  const [
    isArfModalOpen,
    setIsArfModalOpen,
  ] = useState(false);
  const [
    isOmvModalOpen,
    setIsOmvModalOpen,
  ] = useState(false);
  const [
    isVesModalOpen,
    setIsVesModalOpen,
  ] = useState(false);
  const [
    isTotalBasicCostModalOpen,
    setIsTotalBasicCostModalOpen,
  ] = useState(false);
  const [
    isPremiumModalOpen,
    setIsPremiumModalOpen,
  ] = useState(false);
  const [
    isRemarkModalOpen,
    setIsRemarkModalOpen,
  ] = useState(false);
  const [
    selectedDealer,
    setSelectedDealer,
  ] = useState<CamelCaseParallelDealer | null>(null);

  const handleDownpaymentClick = () => {
    setIsDownpaymentModalOpen(true);
  };

  const handleDepreciationClick = () => {
    setIsDepreciationModalOpen(true);
  };

  const handleInstallmentClick = () => {
    setIsInstallmentModalOpen(true);
  };

  const handleRoadTaxClick = () => {
    setIsRoadTaxModalOpen(true);
  };

  const handleArfClick = () => {
    setIsArfModalOpen(true);
  };

  const handleOmvClick = () => {
    setIsOmvModalOpen(true);
  };

  const handleVesClick = () => {
    setIsVesModalOpen(true);
  };

  const handleTotalBasicCostClick = () => {
    setIsTotalBasicCostModalOpen(true);
  };

  const handlePremiumClick = () => {
    setIsPremiumModalOpen(true);
  };

  const handleRemarkClick = (dealerId: number) => {
    const getDealerById = piDealers?.find(
      dealer => dealer.id === dealerId,
    );

    setSelectedDealer(getDealerById ?? null);
    setIsRemarkModalOpen(true);
  };

  const convertPriceToText = (priceInt: number | null | undefined, unit?: string) => { // @suppress longLineCheck
    if (priceInt === null || priceInt === 0) {
      return '-';
    }

    if (priceInt && unit) {
      return `$${priceInt.toLocaleString()} ${unit}`;
    }

    return `$${priceInt?.toLocaleString()}`;
  };

  const priceValue = price ? `$${price.toLocaleString()}` : 'POA';
  const deprecationValue = convertPriceToText(depreciation, '/yr');
  const loanRatio = priceDetail?.loanToValueRatio ?? 1;
  const downPaymentValue = convertPriceToText(price
    ? Math.round(price * loanRatio)
    : null);
  const installmentValue = convertPriceToText(priceDetail?.installment, '/mth');
  const coeQuotaPremiumValue = convertPriceToText(priceDetail?.coeQuotaPremium);
  const roadTaxValue = convertPriceToText(priceDetail?.roadTax, '/yr');
  const omvValue = convertPriceToText(priceDetail?.omv);
  const arfValue = convertPriceToText(priceDetail?.arf);
  const vesValue = convertPriceToText(Math.abs(priceDetail?.ves ?? 0));
  const eeaiValue = convertPriceToText(priceDetail?.eeai);
  const basicCostValue = convertPriceToText(priceDetail?.totalBasicCost);
  const premiumValue = priceDetail?.premium ? `${priceDetail?.premium}%` : '-';
  const previousRecordedPriceValue = convertPriceToText(priceDetail?.previousRecordedPrice); // @suppress longLineCheck
  const highestRecordedPriceValue = convertPriceToText(priceDetail?.highestRecordedPrice); // @suppress longLineCheck
  const lowestRecordedPriceValue = convertPriceToText(priceDetail?.lowestRecordedPrice); // @suppress longLineCheck

  const isNegativeVes = priceDetail?.ves && priceDetail?.ves < 0;
  const isElectric = submodel?.specifications
    .fuelType === VEHICLE_VALUE_ELECTRIC_CARS;

  const rebates = [
    isNegativeVes ? 'After VES' : null,
    isNegativeVes && isElectric ? 'EEAI' : null,
  ].filter(Boolean);

  const arfText = rebates.length ? `(${rebates.join(' & ')} rebates)` : '';

  const getPriceSign = (currentPrice: string, previousPrice: string) => {
    const redArrow = joinSgcmMedia('/public/new-cars/icon/redArrow.svg');
    const greenArrow = joinSgcmMedia('/public/new-cars/icon/greenArrow.svg');

    if (currentPrice > previousPrice) return ` ${redArrow}`;
    if (currentPrice < previousPrice) return ` ${greenArrow}`;
    return null;
  };

  const priceSign = getPriceSign(priceValue, previousRecordedPriceValue);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className={cx(
      styles.containerRoot,
      !isPiModel && styles.isAdModel,
    )}>
      <DepreciationModal
        isOpen={isDepreciationModalOpen}
        onClose={() => {
          setIsDepreciationModalOpen(false);
        }} />
      <DownpaymentModal
        isOpen={isDownpaymentModalOpen}
        onClose={() => {
          setIsDownpaymentModalOpen(false);
        }} />
      <InstallmentModal
        isOpen={isInstallmentModalOpen}
        onClose={() => {
          setIsInstallmentModalOpen(false);
        }} />
      <RoadTaxModal
        isOpen={isRoadTaxModalOpen}
        onClose={() => {
          setIsRoadTaxModalOpen(false);
        }} />
      <ArfModal
        isOpen={isArfModalOpen}
        onClose={() => {
          setIsArfModalOpen(false);
        }} />
      <OmvModal
        isOpen={isOmvModalOpen}
        onClose={() => {
          setIsOmvModalOpen(false);
        }} />
      <VesModal
        isOpen={isVesModalOpen}
        onClose={() => {
          setIsVesModalOpen(false);
        }} />
      <TotalBasicCostModal
        isOpen={isTotalBasicCostModalOpen}
        onClose={() => {
          setIsTotalBasicCostModalOpen(false);
        }} />
      <PremiumModal
        isOpen={isPremiumModalOpen}
        onClose={() => {
          setIsPremiumModalOpen(false);
        }} />
      <RemarkModal
        isOpen={isRemarkModalOpen}
        piName={selectedDealer?.name || ''}
        remark={selectedDealer?.adverts[0].remark || ''}
        submodelFullName={submodelFullName}
        onClose={() => {
          setIsRemarkModalOpen(false);
        }} />
      {!isPiModel && priceDetail && (
        <>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_CURRENT_PRICE}
              </div>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
              styles.isSemiBoldRed,
              styles.isDisplayFlex,
            )}>
              {priceValue}
              {priceFluctuation !== null && (
                <Tooltip
                  className={styles.priceFluctuationTooltip}
                  content={(
                    <div className={
                      styles.containerPriceFluctuationTooltip
                    }>
                      <div>
                        {`Price ${priceFluctuation!.amount > 0 ? 'Increased' : 'Decreased'} by $ ${Math.abs(priceFluctuation!.amount).toLocaleString('en-US')} on ${formatDate(priceFluctuation!.date)}`}
                        <br />
                        <a
                          className={
                            styles.containerSubmodelTooltipPricelistLink // @suppress longLineCheck
                          }
                          href={joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${carDetail?.id}&Subcode=${submodel?.id}`).toString()}>
                          View full price history
                          <IosArrowRight
                            className={styles.iconInTooltip}
                            size={8} />
                        </a>
                      </div>
                    </div>
                  )}>
                  { priceSign && priceValue !== 'POA' && (
                    <div>
                      <Image
                        alt="price sign image"
                        className={styles.imagePrice}
                        height={PRICE_SIGN_ICON_HEIGHT}
                        src={priceSign || ''}
                        width={PRICE_SIGN_ICON_WIDTH} />
                    </div>
                  )}
                </Tooltip>
              ) }
              <span className={cx(
                styles.textSpanContent,
                styles.isLightGrey,
              )}>
                {!withCoe && priceValue !== 'POA' && '(w/o COE)'}
              </span>
            </div>
          </div>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_DEPRECIATION}
              </div>
              <button onClick={handleDepreciationClick}>
                <ImageWithFallback
                  alt="icon exclamation mark"
                  className={styles.iconExclamationMark}
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/information.svg')}`}
                  width={ICON_SIZE_IN_PIXEL} />
              </button>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
            )}>
              {deprecationValue}
            </div>
          </div>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_DOWNPAYMENT}
              </div>
              <button onClick={handleDownpaymentClick}>
                <ImageWithFallback
                  alt="icon exclamation mark"
                  className={styles.iconExclamationMark}
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/information.svg')}`}
                  width={ICON_SIZE_IN_PIXEL} />
              </button>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
              styles.isDualLine,
            )}>
              {loanRatio === 1
                ? (
                  '-'
                )
                : (
                  <>
                    {downPaymentValue}
                    {' '}
                    {price && (
                      <span className={styles.textSpanContent}>
                        {
                          `(Maximum ${(1 - loanRatio) * 100}% loan)`
                        }
                      </span>
                    )}
                  </>
                )}
            </div>
          </div>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_INSTALMENT}
              </div>
              <button onClick={handleInstallmentClick}>
                <ImageWithFallback
                  alt="icon exclamation mark"
                  className={styles.iconExclamationMark}
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/information.svg')}`}
                  width={ICON_SIZE_IN_PIXEL} />
              </button>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
            )}>
              {installmentValue}
            </div>
          </div>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_COE_QUOTA_PREMIUM}
              </div>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
              styles.isDualLine,
            )}>
              {coeQuotaPremiumValue}
              {' '}
              {priceDetail.coeQuotaPremium
              && (
                <span className={styles.textSpanContent}>
                  (Category
                  {' '}
                  {priceDetail?.coeCategory.toUpperCase()}
                  {' '}
                  COE)
                </span>
              )}
            </div>
          </div>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_ROAD_TAX}
              </div>
              <button onClick={handleRoadTaxClick}>
                <ImageWithFallback
                  alt="icon exclamation mark"
                  className={styles.iconExclamationMark}
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/information.svg')}`}
                  width={ICON_SIZE_IN_PIXEL} />
              </button>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
            )}>
              {roadTaxValue}
            </div>
          </div>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_OMV}
              </div>
              <button onClick={handleOmvClick}>
                <ImageWithFallback
                  alt="icon exclamation mark"
                  className={styles.iconExclamationMark}
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/information.svg')}`}
                  width={ICON_SIZE_IN_PIXEL} />
              </button>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
            )}>
              {omvValue}
            </div>
          </div>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_ARF}
              </div>
              <button onClick={handleArfClick}>
                <ImageWithFallback
                  alt="icon exclamation mark"
                  className={styles.iconExclamationMark}
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/information.svg')}`}
                  width={ICON_SIZE_IN_PIXEL} />
              </button>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
            )}>
              {arfValue === '-' ? '$0' : arfValue}
              {' '}
              {(isNegativeVes || isElectric)
              && (
                <span className={styles.textSpanContent}>
                  {
                    arfText
                  }
                </span>
              )}
            </div>
          </div>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_VES}
              </div>
              <button onClick={handleVesClick}>
                <ImageWithFallback
                  alt="icon exclamation mark"
                  className={styles.iconExclamationMark}
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/information.svg')}`}
                  width={ICON_SIZE_IN_PIXEL} />
              </button>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
            )}>
              {vesValue === '-' ? '$0' : vesValue}
              {priceDetail.ves
              && (
                <span className={styles.textSpanContent}>
                  {priceDetail.ves > 0
                    ? PRICE_INFO_SPAN_CONTENT_SURCHARGE
                    : PRICE_INFO_SPAN_CONTENT_REBATE}
                </span>
              )}
            </div>
          </div>
          { eeaiValue !== '-' && (
            <div className={styles.containerRow}>
              <div
                className={cx(
                  styles.containerCell,
                  styles.containerInfoTitle,
                )}>
                <div className={styles.textTitle}>
                  {PRICE_INFO_TITLE_EEAI}
                </div>
              </div>
              <div className={cx(
                styles.containerCell,
                styles.containerInfoValue,
              )}>
                {eeaiValue}
                {priceDetail.eeai && (
                  <span className={styles.textSpanContent}>
                    {PRICE_INFO_SPAN_CONTENT_REBATE}
                  </span>
                )}
              </div>
            </div>
          ) }
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_TOTAL_BASIC_COST}
              </div>
              <button onClick={handleTotalBasicCostClick}>
                <ImageWithFallback
                  alt="icon exclamation mark"
                  className={styles.iconExclamationMark}
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/information.svg')}`}
                  width={ICON_SIZE_IN_PIXEL} />
              </button>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
            )}>
              {basicCostValue}
            </div>
          </div>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_PREMIUM}
              </div>
              <button onClick={handlePremiumClick}>
                <ImageWithFallback
                  alt="icon exclamation mark"
                  className={styles.iconExclamationMark}
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/information.svg')}`}
                  width={ICON_SIZE_IN_PIXEL} />
              </button>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
            )}>
              {premiumValue}
            </div>
          </div>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_PREVIOUS_RECORDED_PRICE}
              </div>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
            )}>
              {previousRecordedPriceValue}
            </div>
          </div>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_HIGHEST_RECORDED_PRICE}
              </div>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
            )}>
              {highestRecordedPriceValue}
              {priceDetail.highestRecordedPrice && (
                <span className={styles.textSpanContent}>
                  {PRICE_INFO_SPAN_CONTENT_ROCORDED_PRICE}
                </span>
              )}
            </div>
          </div>
          <div className={styles.containerRow}>
            <div
              className={cx(
                styles.containerCell,
                styles.containerInfoTitle,
              )}>
              <div className={styles.textTitle}>
                {PRICE_INFO_TITLE_LOWEST_RECORDED_PRICE}
              </div>
            </div>
            <div className={cx(
              styles.containerCell,
              styles.containerInfoValue,
            )}>
              {lowestRecordedPriceValue}
              {priceDetail.lowestRecordedPrice && (
                <span className={styles.textSpanContent}>
                  {PRICE_INFO_SPAN_CONTENT_ROCORDED_PRICE}
                </span>
              )}
            </div>
          </div>
        </>
      )}
      {isPiModel && piDealers && (
        <PiPriceInfoList
          piDealers={piDealers}
          submodelId={submodelId}
          onRemarkClick={handleRemarkClick} />
      )}
    </div>
  );
};
